import React, { createContext, useContext, useState, useCallback } from 'react';
import { Conversation } from '../models/Conversation';
import { fetchInboxConversations, archiveConversation } from '../api/chatApi';

export interface ArchiveContextType {
  archivedConversations: Conversation[];
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  showArchived: boolean;
  loadArchivedConversations: (pageToLoad?: number, append?: boolean) => Promise<void>;
  toggleView: (archived: boolean) => void;
  archiveConversation: (conversation: Conversation) => Promise<boolean>;
  setArchivedConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  resetArchive: () => void;
  refreshArchivedConversations: () => Promise<void>;
}

export const ArchiveContext = createContext<ArchiveContextType>({
  archivedConversations: [],
  isLoading: false,
  hasMore: true,
  page: 1,
  showArchived: false,
  loadArchivedConversations: async () => {},
  toggleView: () => {},
  archiveConversation: async () => false,
  setArchivedConversations: () => {},
  resetArchive: () => {},
  refreshArchivedConversations: async () => {},
});

export const useArchiveContext = () => useContext(ArchiveContext);

interface ArchiveProviderProps {
  children: React.ReactNode;
}

export const ArchiveProvider: React.FC<ArchiveProviderProps> = ({ children }) => {
  const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

  // Load archived conversations
  const loadArchivedConversations = useCallback(async (pageToLoad = 1, append = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await fetchInboxConversations(pageToLoad, true);
      if (append) {
        setArchivedConversations(prev => [...prev, ...response.data]);
      } else {
        setArchivedConversations(response.data);
      }
      setPage(pageToLoad);
      setHasMore(pageToLoad < response.pageCount);
      setInitialLoadCompleted(true);
    } catch (error) {
      console.error('Failed to load archived conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  // Toggle between inbox and archive view
  const toggleView = useCallback((archived: boolean) => {
    setShowArchived(archived);
    if (archived && !initialLoadCompleted) {
      loadArchivedConversations(1, false);
    }
  }, [initialLoadCompleted, loadArchivedConversations]);

  // Archive or unarchive a conversation
  const archiveConversationAction = useCallback(async (conversation: Conversation): Promise<boolean> => {
    try {
      const response = await archiveConversation(conversation.id);
      if (response.success) {
        if (showArchived) {
          // Remove from archived
          setArchivedConversations(prev => {
            const newArchived = prev.filter(c => c.id !== conversation.id);
            // If we're removing the last conversation and there's no more pages, set hasMore to false
            if (newArchived.length === 0 && !isLoading) {
              setHasMore(false);
            }
            return newArchived;
          });
        } else {
          // Add to archived
          setArchivedConversations(prev => {
            // Check if conversation already exists
            const existingIds = prev.map(c => c.id);
            if (existingIds.includes(conversation.id)) {
              return prev;
            }
            
            // Find the correct position based on lastMessageTime
            const newConversations = [...prev];
            const insertIndex = newConversations.findIndex(
              c => new Date(c.lastMessageTime).getTime() < new Date(conversation.lastMessageTime).getTime()
            );
            
            if (insertIndex === -1) {
              // If no conversation has an older timestamp, add to the end
              newConversations.push(conversation);
            } else {
              // Insert at the correct position
              newConversations.splice(insertIndex, 0, conversation);
            }
            
            // If archived was empty after initial load, we now have content but no more pages to load initially
            if (prev.length === 0 && initialLoadCompleted) {
              setHasMore(false);
            }
            
            return newConversations;
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      return false;
    }
  }, [showArchived, isLoading, initialLoadCompleted]);

  const resetArchive = useCallback(() => {
    setArchivedConversations([]);
    setPage(1);
    setHasMore(true);
    setIsLoading(false);
    setShowArchived(false);
    setInitialLoadCompleted(false);
  }, []);

  // Refresh archived conversations (reload current page)
  const refreshArchivedConversations = useCallback(async () => {
    if (page > 0) {
      await loadArchivedConversations(page, false);
    }
  }, [page, loadArchivedConversations]);

  const contextValue: ArchiveContextType = {
    archivedConversations,
    isLoading,
    hasMore,
    page,
    showArchived,
    loadArchivedConversations,
    toggleView,
    archiveConversation: archiveConversationAction,
    setArchivedConversations,
    resetArchive,
    refreshArchivedConversations,
  };

  return (
    <ArchiveContext.Provider value={contextValue}>
      {children}
    </ArchiveContext.Provider>
  );
};